// ============================================================================
// FinanceKpiCards (E2, F-521) — les 4 KPI du mois : revenus / depenses / net / marge
// ============================================================================
// Portage de FinanceKpiCard du PoC (StatCard + variances) sur la primitive MAISON
// GsKpiComparison (livree a E1a et deja consommee par le rail « Indicateurs ») :
// valeur heros + N deltas ▲/▼, negatif -> rouge. Decision MASTER §6 : reutiliser
// l'existant plutot que porter une carte de plus.
//
// ECART ASSUME vs PoC : la SPARKLINE de FinanceKpiCard n'est pas portee en V1 —
// GsKpiComparison n'a pas de slot « tendance » et @gs/ui est INTERDIT a ce frag
// (un chore parallele y travaille). L'information de tendance reste servie, en plus
// riche, par la carte « Tendance 6 mois » du detail (F-523). A revoir si `trend`
// est promu dans GsKpiComparison.
//
// M-503 : les variances sont DEJA normalisees « favorable = positif » par
// computeStatement (revenu : actual-budget ; depense : budget-actual) — le signe du
// ratio pilote donc directement la couleur, sans re-interpreter la section ici.

import { GsKpiComparison } from '@gs/ui';
import type { GsKpiComparisonDelta } from '@gs/ui';
import type { TFunction } from 'i18next';
import type { Statement } from '../../lib/finance/computations';
import type { FinanceSummary } from '../../hooks/useFinance';
import { formatCad, formatDeltaPct, formatLevelPct } from '../../pages/finance/format';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800';

/** Deltas « vs budget » / « vs an. prec. » d'une ligne de statement (M-503). */
function deltas(
  pctVsBudget: number, pctVsPriorYear: number, locale: string, t: TFunction,
): GsKpiComparisonDelta[] {
  const delta = (ratio: number, label: string): GsKpiComparisonDelta => ({
    value: formatDeltaPct(ratio, locale),
    label,
    direction: ratio >= 0 ? 'up' : 'down',
    favorable: ratio >= 0,
  });
  return [
    delta(pctVsBudget, t('gest:finance.kpi.vsBudget')),
    delta(pctVsPriorYear, t('gest:finance.kpi.vsPriorYear')),
  ];
}

export interface FinanceKpiCardsProps {
  statement: Statement;
  summary: FinanceSummary;
  locale: string;
  t: TFunction;
}

export function FinanceKpiCards({ statement, summary, locale, t }: FinanceKpiCardsProps) {
  const { totalRevenue, totalExpense, netIncome } = statement;
  const margin = summary.grossMargin;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div className={CARD}>
        <GsKpiComparison
          label={t('gest:finance.kpi.revenue')}
          value={formatCad(totalRevenue.actual, locale)}
          negative={totalRevenue.actual < 0}
          deltas={deltas(totalRevenue.pctVsBudget, totalRevenue.pctVsPriorYear, locale, t)}
        />
      </div>
      <div className={CARD}>
        <GsKpiComparison
          label={t('gest:finance.kpi.expense')}
          value={formatCad(totalExpense.actual, locale)}
          deltas={deltas(totalExpense.pctVsBudget, totalExpense.pctVsPriorYear, locale, t)}
        />
      </div>
      <div className={CARD}>
        <GsKpiComparison
          label={t('gest:finance.kpi.netIncome')}
          value={formatCad(netIncome.actual, locale)}
          negative={netIncome.actual < 0}
          deltas={deltas(netIncome.pctVsBudget, netIncome.pctVsPriorYear, locale, t)}
        />
      </div>
      <div className={CARD}>
        {/* Marge brute = NIVEAU (%) : ses ecarts sont des points de pourcentage
            (marge - marge budget), pas des ratios de variance — d'ou le calcul
            local plutot que pctVsBudget (parite PoC). */}
        <GsKpiComparison
          label={t('gest:finance.kpi.grossMargin')}
          value={formatLevelPct(margin.actual, locale)}
          negative={margin.actual < 0}
          deltas={deltas(margin.actual - margin.budget, margin.actual - margin.priorYear, locale, t)}
        />
      </div>
    </div>
  );
}
